import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { NotificationType, type OpenShiftRequest, type ReplaceShiftRequest } from "../../../../utils/models";
import { isPostedRequest } from "../../../../utils/openShiftMatching";

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	/** How many answered, counted as the desktop Requests screen counts responses (`countNurseResponses`). */
	responseCount: number;
	testId: string;
};

/** The small print's parts share one quiet type; a chip or a flag breaks it only where it carries meaning. */
const smallPrintSx = { fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, lineHeight: 1.35 } as const;

/**
 * A request card's one line of small print — what kind of ask it is (a replacement, a swap), how many are still needed,
 * the unit, and how many answered — its parts joined by middots so they read as facts rather than run together. Worded as
 * Option A's "Need 2 · 3 responses" (APP-886).
 */
export const GapsRequestSmallPrint: FC<Props> = ({ request, responseCount, testId }) => {
	const { t } = useTranslation();
	const { getNurseFormattedName } = useHospitalData();
	const isOpenShift = request.request_type === NotificationType.OpenShift;
	const neededNurses = isPostedRequest(request) ? request.needed_nurses : undefined;

	const kindLabel = (() => {
		switch (request.request_type) {
			case NotificationType.ReplaceShift:
				// The request union is not discriminated by `request_type` in the model types; a replace_shift IS a ReplaceShiftRequest.
				return t("gaps.requests.replacement", { name: getNurseFormattedName((request as ReplaceShiftRequest).replacement_nurse_id) });
			case NotificationType.ReplaceAgency:
				return t("gaps.requests.replaceAgency");
			case NotificationType.Swap:
				return t("gaps.requests.swap");
			default:
				return null;
		}
	})();

	const parts: { key: string; text: string; emphasis?: string }[] = [
		...(kindLabel === null ? [] : [{ key: "kind", text: kindLabel, emphasis: INK.SECONDARY }]),
		...(isOpenShift && neededNurses !== undefined ? [{ key: "needed", text: t("gaps.requests.need", { count: neededNurses }) }] : []),
		...(request.location ? [{ key: "location", text: request.location }] : []),
		responseCount > 0
			? { key: "responses", text: t("gaps.requests.responseCount", { count: responseCount }) }
			: { key: "responses", text: t("gaps.requests.noResponses") },
	];

	return (
		<Typography component="span" data-testid={`${testId}-small-print`} sx={smallPrintSx}>
			{parts.map((part, index) => (
				<Box key={part.key} component="span" data-testid={`${testId}-${part.key}`} sx={part.emphasis ? { fontWeight: 600, color: part.emphasis } : undefined}>
					{index > 0 && (
						<Box component="span" sx={{ color: INK.MUTED, fontWeight: 400 }}>
							{" · "}
						</Box>
					)}
					{part.text}
				</Box>
			))}
		</Typography>
	);
};
